import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { PlansService } from '../plans/plans.service';

export interface RegistrationStatus {
  isIncomplete: boolean;
  canUseTrial: boolean;
}

@Injectable({ providedIn: 'root' })
export class RegistrationStatusService {
  private plansService = inject(PlansService);

  private status$ = new BehaviorSubject<RegistrationStatus>({
    isIncomplete: false,
    canUseTrial: false
  });

  /**
   * Check if user has completed registration (has active subscription).
   * Retries up to 4 times with 2s delay if incomplete_registration is returned,
   * as a fallback for the race condition where Stripe webhook hasn't processed yet.
   * Primary resolution is via WebSocket subscriptionActivated event (see banner.component.ts).
   */
  checkStatus(): void {
    this.fetchAndUpdate(0);
  }

  private fetchAndUpdate(attempt: number): void {
    this.plansService.getCurrentPlan().subscribe({
      next: (response: any) => {
        if (response?.data?.status === 'incomplete_registration') {
          if (attempt < 4) {
            timer(2000).subscribe(() => this.fetchAndUpdate(attempt + 1));
          } else {
            this.status$.next({
              isIncomplete: true,
              canUseTrial: response.data.canUseTrial ?? false
            });
          }
        } else {
          this.status$.next({ isIncomplete: false, canUseTrial: false });
        }
      },
      error: () => {
        this.status$.next({ isIncomplete: false, canUseTrial: false });
      }
    });
  }

  get registrationStatus$(): Observable<RegistrationStatus> {
    return this.status$.asObservable();
  }

  /**
   * Manually mark registration as complete (after successful checkout)
   */
  markAsComplete(): void {
    this.status$.next({
      isIncomplete: false,
      canUseTrial: false
    });
  }
}
